// Notes Storage Utilities
// Manages free-form notes (like Apple Notes) stored locally

import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';

const NOTES_STORAGE_KEY = 'gematria_user_notes';
const MAX_NOTES = 200;

// Platform-specific storage wrapper
const storage = {
  getItem: async (key) => {
    if (Platform.OS === 'web') {
      return localStorage.getItem(key);
    }
    return await AsyncStorage.getItem(key);
  },
  setItem: async (key, value) => {
    if (Platform.OS === 'web') {
      localStorage.setItem(key, value);
    } else {
      await AsyncStorage.setItem(key, value);
    }
  },
  removeItem: async (key) => {
    if (Platform.OS === 'web') {
      localStorage.removeItem(key);
    } else {
      await AsyncStorage.removeItem(key);
    }
  }
};

/**
 * Note Structure:
 * {
 *   id: string (timestamp-based)
 *   title: string
 *   content: string
 *   createdAt: number
 *   updatedAt: number
 * }
 */

/**
 * Get all notes
 */
export const getAllNotes = async () => {
  try {
    const stored = await storage.getItem(NOTES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading notes:', error);
    return [];
  }
};

/**
 * Create a new note
 */
export const createNote = async (title = '', content = '') => {
  const notes = await getAllNotes();

  if (notes.length >= MAX_NOTES) {
    throw new Error(`Notes are full! Maximum ${MAX_NOTES} notes allowed.`);
  }

  const note = {
    id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
    title: title.trim() || 'Untitled Note',
    content: content.trim(),
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };

  notes.unshift(note);
  await storage.setItem(NOTES_STORAGE_KEY, JSON.stringify(notes));
  return note;
};

/**
 * Update an existing note
 */
export const updateNote = async (id, updates) => {
  const notes = await getAllNotes();
  const index = notes.findIndex(n => n.id === id);

  if (index === -1) return null;

  notes[index] = {
    ...notes[index],
    ...updates,
    updatedAt: Date.now(),
  };

  // Move updated note to top
  const updated = notes.splice(index, 1)[0];
  notes.unshift(updated);

  await storage.setItem(NOTES_STORAGE_KEY, JSON.stringify(notes));
  return updated;
};

/**
 * Delete a note
 */
export const deleteNote = async (id) => {
  const notes = await getAllNotes();
  const filtered = notes.filter(n => n.id !== id);
  await storage.setItem(NOTES_STORAGE_KEY, JSON.stringify(filtered));
  return filtered;
};

/**
 * Delete all notes
 */
export const deleteAllNotes = async () => {
  await storage.removeItem(NOTES_STORAGE_KEY);
  return [];
};

/**
 * Search notes by title or content
 */
export const searchNotes = async (query) => {
  const notes = await getAllNotes();
  if (!query.trim()) return notes;

  const q = query.toLowerCase();
  return notes.filter(n =>
    n.title.toLowerCase().includes(q) ||
    n.content.toLowerCase().includes(q)
  );
};
